if (!customElements.get('pdp-story')) {
  class PdpStory extends HTMLElement {
    connectedCallback() {
      this.steps = Array.from(this.querySelectorAll('[data-story-step]'));
      this.stepsList = this.querySelector('.pdp-story__steps');
      this.figure = this.querySelector('.pdp-story__media');
      this.layers = Array.from(this.querySelectorAll('[data-story-layer]'));
      this.captionMeta = this.querySelector('.pdp-story__caption-meta');
      this.captionText = this.querySelector('.pdp-story__caption-text');
      this.activeStep = this.steps.find((step) => step.classList.contains('is-active')) || this.steps[0] || null;
      this.frame = 0;
      this.topOffset = 0;
      this.sectionVisible = false;
      this.scrollBound = false;
      this.mobileClickBound = false;
      this.autoplayTimer = 0;
      this.autoplayEnabled = this.dataset.autoplay !== 'false';
      this.intervalMs = this.readInterval();
      this.desktopQuery = window.matchMedia('(min-width: 990px)');
      this.motionQuery = window.matchMedia('(prefers-reduced-motion: reduce)');

      this.onScroll = () => {
        if (this.frame || !this.desktopQuery.matches) return;
        this.frame = requestAnimationFrame(() => {
          this.frame = 0;
          if (!this.desktopQuery.matches) return;
          this.updateImagePin();
          this.updateActive();
        });
      };

      this.onResize = () => {
        clearTimeout(this.resizeTimer);
        this.resizeTimer = setTimeout(() => this.applyMode(), 150);
      };

      this.onStepClick = (event) => {
        if (this.desktopQuery.matches) return;
        const step = event.target.closest('[data-story-step]');
        if (!step || !this.contains(step)) return;
        this.setActive(step);
        this.syncAutoplay();
      };

      this.onStepKeydown = (event) => {
        if (this.desktopQuery.matches) return;
        if (event.key !== 'Enter' && event.key !== ' ') return;
        const step = event.target.closest('[data-story-step]');
        if (!step || !this.contains(step)) return;
        event.preventDefault();
        this.setActive(step);
        this.syncAutoplay();
      };

      this.onVisibility = () => this.syncAutoplay();
      this.onMotionChange = () => this.syncAutoplay();

      window.addEventListener('resize', this.onResize, { passive: true });
      this.desktopQuery.addEventListener('change', this.onResize);
      this.motionQuery.addEventListener('change', this.onMotionChange);
      document.addEventListener('visibilitychange', this.onVisibility);
      this.bindSectionObserver();
      this.applyMode();
    }

    disconnectedCallback() {
      this.stopAutoplay();
      this.sectionObserver?.disconnect();
      this.stepObserver?.disconnect();
      this.unlistenScroll();
      this.disableMobileClicks();
      window.removeEventListener('resize', this.onResize);
      this.desktopQuery?.removeEventListener('change', this.onResize);
      this.motionQuery?.removeEventListener('change', this.onMotionChange);
      document.removeEventListener('visibilitychange', this.onVisibility);
      clearTimeout(this.resizeTimer);
      if (this.frame) cancelAnimationFrame(this.frame);
      this.sectionObserver = null;
      this.stepObserver = null;
    }

    bindSectionObserver() {
      if (!('IntersectionObserver' in window)) {
        this.sectionVisible = true;
        return;
      }

      this.sectionObserver = new IntersectionObserver((entries) => {
        this.sectionVisible = entries.some((entry) => entry.isIntersecting);
        this.syncAutoplay();
        if (!this.desktopQuery.matches) return;
        if (this.sectionVisible) {
          this.measureStickyTop();
          this.syncDesktopRunway();
          this.updateImagePin();
          this.listenScroll();
          this.updateActive();
        } else {
          this.unlistenScroll();
          this.classList.remove('is-image-pinned');
        }
      });
      this.sectionObserver.observe(this);
    }

    applyMode() {
      if (this.desktopQuery.matches) {
        this.stopAutoplay();
        this.disableMobileClicks();
        this.enableDesktop();
      } else {
        this.disableDesktop();
        this.enableMobileClicks();
        this.syncAutoplay();
      }
    }

    enableDesktop() {
      this.measureStickyTop();
      this.syncDesktopRunway();
      this.updateImagePin();
      this.bindStepObserver();
      if (this.sectionVisible) this.listenScroll();
      this.updateActive();
    }

    disableDesktop() {
      this.unlistenScroll();
      this.stepObserver?.disconnect();
      this.classList.remove('is-stuck', 'is-image-pinned');
      if (this.stepsList) this.stepsList.style.paddingBottom = '';
    }

    enableMobileClicks() {
      this.steps.forEach((step) => {
        step.setAttribute('role', 'button');
        step.setAttribute('tabindex', '0');
        step.setAttribute('aria-pressed', step.classList.contains('is-active') ? 'true' : 'false');
      });
      if (this.mobileClickBound || !this.stepsList) return;
      this.stepsList.addEventListener('click', this.onStepClick);
      this.stepsList.addEventListener('keydown', this.onStepKeydown);
      this.mobileClickBound = true;
    }

    disableMobileClicks() {
      this.steps.forEach((step) => {
        step.removeAttribute('role');
        step.removeAttribute('tabindex');
        step.removeAttribute('aria-pressed');
      });
      if (!this.mobileClickBound || !this.stepsList) return;
      this.stepsList.removeEventListener('click', this.onStepClick);
      this.stepsList.removeEventListener('keydown', this.onStepKeydown);
      this.mobileClickBound = false;
    }

    readInterval() {
      const seconds = Number(this.dataset.autoplayInterval);
      if (!Number.isFinite(seconds) || seconds < 2) return 4000;
      return seconds * 1000;
    }

    canAutoplay() {
      return (
        this.autoplayEnabled &&
        !this.desktopQuery.matches &&
        !this.motionQuery.matches &&
        this.sectionVisible &&
        document.visibilityState !== 'hidden' &&
        this.steps.length > 1
      );
    }

    syncAutoplay() {
      this.stopAutoplay();
      if (!this.canAutoplay()) return;
      this.autoplayTimer = window.setInterval(() => this.advance(), this.intervalMs);
    }

    stopAutoplay() {
      if (!this.autoplayTimer) return;
      window.clearInterval(this.autoplayTimer);
      this.autoplayTimer = 0;
    }

    advance() {
      if (!this.canAutoplay()) {
        this.stopAutoplay();
        return;
      }

      const index = Math.max(0, this.steps.indexOf(this.activeStep));
      const next = this.steps[(index + 1) % this.steps.length];
      this.setActive(next);
      this.reveal(next);
    }

    reveal(step) {
      if (!step) return;
      const viewport = window.innerHeight || document.documentElement.clientHeight;
      const rect = step.getBoundingClientRect();
      if (rect.top >= 0 && rect.bottom <= viewport) return;
      step.scrollIntoView({ behavior: 'smooth', block: 'nearest' });
    }

    listenScroll() {
      if (this.scrollBound) return;
      window.addEventListener('scroll', this.onScroll, { passive: true });
      this.scrollBound = true;
    }

    unlistenScroll() {
      if (!this.scrollBound) return;
      window.removeEventListener('scroll', this.onScroll);
      this.scrollBound = false;
    }

    measureStickyTop() {
      if (!this.desktopQuery.matches) return;
      let offset = 0;
      document.querySelectorAll('.shopify-section-group-header-group, .section-header').forEach((element) => {
        const position = getComputedStyle(element).position;
        if (position !== 'sticky' && position !== 'fixed') return;
        offset = Math.max(offset, element.getBoundingClientRect().height);
      });
      if (offset <= 0) {
        offset = parseFloat(getComputedStyle(document.documentElement).getPropertyValue('--header-height')) || 0;
      }
      const top = Math.round(Math.max(0, offset) + 12);
      if (top === this.topOffset) return;
      this.topOffset = top;
      this.style.setProperty('--pdp-story-sticky-top', `${top}px`);
    }

    updateImagePin() {
      if (!this.desktopQuery.matches || !this.figure) {
        this.classList.remove('is-image-pinned');
        return;
      }
      const top = this.figure.getBoundingClientRect().top;
      this.classList.toggle('is-image-pinned', Math.abs(top - this.topOffset) <= 1);
    }

    syncDesktopRunway() {
      if (!this.desktopQuery.matches) return;
      const last = this.steps[this.steps.length - 1];
      const imageHeight = this.figure?.getBoundingClientRect().height || 0;
      if (!this.stepsList || !last || !imageHeight) return;
      const padding = Math.max(0, Math.round(imageHeight / 2 - last.getBoundingClientRect().height));
      const value = `${padding}px`;
      if (this.stepsList.style.paddingBottom !== value) this.stepsList.style.paddingBottom = value;
    }

    bindStepObserver() {
      if (!this.desktopQuery.matches || !('IntersectionObserver' in window) || !this.steps.length) return;
      this.stepObserver?.disconnect();
      const metrics = this.metrics();
      this.stepObserver = new IntersectionObserver(() => this.updateActive(), {
        root: null,
        rootMargin: metrics.rootMargin,
        threshold: [0, 0.25, 0.5, 0.75, 1],
      });
      this.steps.forEach((step) => this.stepObserver.observe(step));
    }

    metrics() {
      const viewport = window.innerHeight || 1;
      const imageHeight = this.figure?.getBoundingClientRect().height || 0;
      const line = Math.round(this.topOffset + imageHeight / 2);
      let top = Math.max(0, line - 12);
      let bottom = Math.max(24, viewport - top - 24);
      if (top + bottom > viewport - 8) {
        top = Math.max(0, Math.min(top, viewport - 32));
        bottom = Math.max(8, viewport - top - 8);
      }

      return {
        line,
        rootMargin: `-${top}px 0px -${bottom}px 0px`,
      };
    }

    updateActive() {
      if (!this.desktopQuery.matches || !this.steps.length) return;
      const line = this.metrics().line;
      const storyBottom = this.getBoundingClientRect().bottom;
      let active = this.steps[0];

      if (storyBottom <= line + 24) {
        active = this.steps[this.steps.length - 1];
      } else {
        for (const step of this.steps) {
          if (step.getBoundingClientRect().top <= line) active = step;
        }
      }

      this.setActive(active);
    }

    setActive(step) {
      if (!step || step === this.activeStep) return;
      this.activeStep = step;
      const mobile = !this.desktopQuery.matches;

      this.steps.forEach((item) => {
        const active = item === step;
        item.classList.toggle('is-active', active);
        if (mobile) item.setAttribute('aria-pressed', active ? 'true' : 'false');
      });

      this.updateImage(step);
      this.preloadNext(step);
    }

    updateImage(step) {
      const src = step.dataset.imageSrc || '';
      const caption = step.dataset.caption || '';
      const index = step.dataset.index || '';
      const matchedLayer =
        this.layers.find((layer) => layer.dataset.index === index) ||
        this.layers.find((layer) => src && layer.dataset.imageSrc === src);

      if (matchedLayer) {
        this.layers.forEach((layer) => {
          layer.classList.toggle('is-active', layer === matchedLayer);
        });

        const image = matchedLayer.querySelector('.pdp-story__image');
        if (image) image.alt = caption ? '' : step.dataset.title || '';
      }

      if (this.captionMeta) {
        const period = step.dataset.period || '';
        this.captionMeta.textContent = period ? `${step.dataset.index} \u00b7 ${period}` : step.dataset.index || '';
      }

      if (this.captionText) {
        this.captionText.textContent = caption;
        this.captionText.hidden = caption === '';
      }
    }

    preloadNext(step) {
      const next = this.steps[this.steps.indexOf(step) + 1];
      const src = next?.dataset.imageSrc;
      if (!src || this.preloadedSrc === src) return;
      this.preloadedSrc = src;
      const preload = new Image();
      if (next.dataset.imageSrcset) preload.srcset = next.dataset.imageSrcset;
      preload.src = src;
    }
  }

  customElements.define('pdp-story', PdpStory);
}
